import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(() => {
    // Initialize from localStorage or i18n
    return localStorage.getItem('zenta_language') || i18n.language || 'de';
  });

  // Sync with i18n changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setLanguage(lng);
      // Always persist to localStorage
      localStorage.setItem('zenta_language', lng);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, [i18n]);

  // On mount, ensure i18n matches localStorage (user's saved preference)
  useEffect(() => {
    const savedLanguage = localStorage.getItem('zenta_language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const changeLanguage = async (lng) => {
    // Update localStorage first for persistence
    localStorage.setItem('zenta_language', lng);
    
    // Change i18n language
    await i18n.changeLanguage(lng);
    
    // Also update user profile if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/language`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ language: lng })
        });
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
